import { noop } from 'lodash-es';

import { BufferConfig } from '@dtvgo/player-common-utils';
import {
  getCDNTokenizationConfig,
  getPlaybackInfoWithAuthorizerV3,
  hasTokenization,
} from '@dtvgo/player-utils';
import { ErrorCode, Source } from '@dtvgo/error';

import { middleware } from '~/services/middleware';

import { tokensAreUsable } from '~/store/credentials';
import { TokenType } from '~/utils/appConfig';
import { errorFactory } from '~/utils/errorFactory';

type LoadRequestData = framework.messages.LoadRequestData;

function parseLoadRequest(request: LoadRequestData) {
  const {
    assetId: contentId,
    forgerockTokensData: { idToken = undefined } = {},
    selectedProfile: { id: profileId = undefined } = {},
    isLive = false,
  } = request.media.customData || {};

  if (!idToken || !profileId || !contentId) {
    throw errorFactory.create('Some required params are missing', {
      source: Source.GENERAL,
      code: ErrorCode.MISSING_DATA,
      data: { idToken, profileId, contentId },
    });
  }

  return {
    idToken,
    profileId,
    contentId,
    isLive,
  };
}

export function getPlaybackInfo(
  options: {
    lowLatency: boolean;
    cdnTokenizationTokenType: TokenType[];
    buffer: {
      live: BufferConfig;
      vod: BufferConfig;
    };
  },
  events?: {
    onLoadRequest?: (request: {
      isLive: boolean;
      contentId: string;
      idToken: string;
      profileId: string;
    }) => Promise<void>;
    onPlaybackInfoAvailable?: (
      content: { isLive: boolean; contentId: string },
      playbackInfo: Awaited<ReturnType<typeof getPlaybackInfoWithAuthorizerV3>>,
    ) => void;
    onAuthorizerError?: (error: Error) => void;
    onFinished?: () => void;
  },
) {
  return async (request: LoadRequestData) => {
    try {
      const { idToken, profileId, contentId, isLive } =
        parseLoadRequest(request);

      if (events?.onLoadRequest) {
        await events.onLoadRequest({ idToken, profileId, isLive, contentId });
      }

      const playbackInfo = await getPlaybackInfoWithAuthorizerV3(
        middleware,
        { isLive, contentId },
        {
          onAuthorizerError: events?.onAuthorizerError || noop,
          refreshSecondsBefore: 90,
        },
        tokensAreUsable,
      );

      const { provider, channelId, sources, clearRefreshTimeout } =
        playbackInfo;

      events?.onPlaybackInfoAvailable?.({ isLive, contentId }, playbackInfo);

      // TODO: Allow switch to other streams on failure
      const [source] =
        options.lowLatency ? sources : (
          sources.filter(({ isLowLatency }) => !isLowLatency)
        );

      // This logic means we prefer dash, but fallback to hls if dash is not available (P+ content)
      const contentUrl = source?.dash || source?.hls;
      const isHls = !source?.dash && !!source?.hls;

      const onFinished = () => {
        clearRefreshTimeout();
        events?.onFinished?.();
      };

      if (!contentUrl) {
        onFinished();
        throw errorFactory.create('No playable source found', {
          source: Source.GENERAL,
          code: ErrorCode.MISSING_DATA,
        });
      }

      const { widevine, playready } = source.keySystemsConfig || {};
      const drmConfig = widevine || playready;

      let onRequest;
      let onResponseReceived;

      if (hasTokenization({ manifestUrl: contentUrl })) {
        ({ onRequest, onResponseReceived } = getCDNTokenizationConfig(
          options.cdnTokenizationTokenType,
        ));
      }

      return {
        contentId,
        contentUrl,
        streamType:
          isLive ?
            cast.framework.messages.StreamType.LIVE
          : cast.framework.messages.StreamType.BUFFERED,
        drmConfig,
        bufferConfig: isLive ? options.buffer.live : options.buffer.vod,
        isLowLatency: isLive && source?.isLowLatency,
        isHls,
        customData: { provider: provider || undefined, channelId },
        onRequest,
        onResponseReceived,
        onFinished,
      };
    } catch (error) {
      console.error('[cast>VrioOtt>getPlaybackInfo] error', error);
      const { ErrorData, ErrorReason, ErrorType } = cast.framework.messages;
      const errorData = new ErrorData(ErrorType.LOAD_FAILED);
      errorData.reason = ErrorReason.APP_ERROR;
      errorData.customData = { error: errorFactory.ensureVrioOttError(error) };
      return errorData;
    }
  };
}
